/**
 * Moving and resizing boxes by dragging them. Pure maths, so it can be tested without a browser.
 *
 * A box is a centre, a size and a yaw. Resizing by a face keeps the opposite face still: dragging the
 * north wall of a room changes the room's depth and moves its centre by half as much, which is what
 * someone dragging a wall expects.
 */
import type { Box, Vec3 } from '../model/types';

export type Axis = 'x' | 'y' | 'z';
export type Face = '+x' | '-x' | '+y' | '-y' | '+z' | '-z';

export const SNAP_M = 0.1;
/** Nothing may be squashed below this, or it becomes impossible to grab again. */
export const MIN_SIZE_M = 0.1;

export const faceAxis = (f: Face): Axis => f[1] as Axis;
export const faceSign = (f: Face): 1 | -1 => (f[0] === '+' ? 1 : -1);
export const axisIndex = (a: Axis): 0 | 1 | 2 => (a === 'x' ? 0 : a === 'y' ? 1 : 2);

/** Unit vector of the box's own axis, in world space. */
export function axisDirection(axis: Axis, yawRad: number): Vec3 {
  const c = Math.cos(yawRad);
  const s = Math.sin(yawRad);
  if (axis === 'x') return [c, s, 0];
  if (axis === 'y') return [-s, c, 0];
  return [0, 0, 1];
}

export const snap = (v: number, step = SNAP_M): number => Math.round(v / step) * step;
/** Rounds away the floating-point dust that repeated snapping leaves behind. */
const tidy = (v: number): number => Math.round(v * 1e6) / 1e6;

/**
 * Moves one face along its own axis by `metres`, keeping the opposite face where it is.
 * `metres` is measured along the positive direction of that axis, whichever face is being dragged.
 */
export function resizeBox(box: Box, face: Face, metres: number, min = MIN_SIZE_M): Box {
  const axis = faceAxis(face);
  const i = axisIndex(axis);
  const sign = faceSign(face);
  const current = box.size[i];
  let m = snap(metres);
  // never below the minimum: clamp the movement, not the result
  if (current + sign * m < min) m = sign * (min - current);
  const dir = axisDirection(axis, box.yawRad);
  const size: Vec3 = [...box.size];
  size[i] = tidy(current + sign * m);
  const center: Vec3 = [
    tidy(box.center[0] + (dir[0] * m) / 2),
    tidy(box.center[1] + (dir[1] * m) / 2),
    tidy(box.center[2] + (dir[2] * m) / 2),
  ];
  return { ...box, center, size };
}

/** Drags a vertical edge or a top corner: two faces at once. */
export function resizeBoxCorner(box: Box, faces: [Face, Face], a: number, b: number, min = MIN_SIZE_M): Box {
  return resizeBox(resizeBox(box, faces[0], a, min), faces[1], b, min);
}

/**
 * Puts the box's centre at `to`. A drag works this way rather than by repeated deltas: a drag is
 * previewed many times on its way to one result, and anything relative would compound.
 */
export function placeBox(box: Box, to: Vec3, allowBelowGround = false): Box {
  const center: Vec3 = [tidy(snap(to[0])), tidy(snap(to[1])), tidy(snap(to[2]))];
  if (!allowBelowGround) center[2] = Math.max(center[2], box.size[2] / 2);
  return { ...box, center };
}

/** Puts a point at `to`, kept on or above the ground. */
export function placePoint(to: Vec3, allowBelowGround = false): Vec3 {
  const out: Vec3 = [tidy(snap(to[0])), tidy(snap(to[1])), tidy(snap(to[2]))];
  if (!allowBelowGround) out[2] = Math.max(out[2], 0);
  return out;
}

/** Moves the whole box. Z is kept at or above the ground unless `allowBelowGround`. */
export function moveBox(box: Box, delta: Vec3, allowBelowGround = false): Box {
  return placeBox(box, [box.center[0] + delta[0], box.center[1] + delta[1], box.center[2] + delta[2]], allowBelowGround);
}

export function movePoint(p: Vec3, delta: Vec3, allowBelowGround = false): Vec3 {
  return placePoint([p[0] + delta[0], p[1] + delta[1], p[2] + delta[2]], allowBelowGround);
}

export interface HandleSpec {
  id: string;
  /** World position of the grab point. */
  position: Vec3;
  /** One face for a wall or the roof; two for a corner. */
  faces: Face[];
  kind: 'face' | 'corner';
}

/**
 * Where to put the grab points: the middle of each wall, the middle of the roof, and the four roof
 * corners. Corners change two dimensions at once.
 */
export function handlesFor(box: Box): HandleSpec[] {
  const [cx, cy, cz] = box.center;
  const [sx, sy, sz] = box.size;
  const c = Math.cos(box.yawRad);
  const s = Math.sin(box.yawRad);
  /** local offset → world point */
  const at = (lx: number, ly: number, lz: number): Vec3 => [cx + c * lx - s * ly, cy + s * lx + c * ly, cz + lz];
  const hx = sx / 2;
  const hy = sy / 2;
  const hz = sz / 2;
  const out: HandleSpec[] = [
    { id: '+x', position: at(hx, 0, 0), faces: ['+x'], kind: 'face' },
    { id: '-x', position: at(-hx, 0, 0), faces: ['-x'], kind: 'face' },
    { id: '+y', position: at(0, hy, 0), faces: ['+y'], kind: 'face' },
    { id: '-y', position: at(0, -hy, 0), faces: ['-y'], kind: 'face' },
    { id: '+z', position: at(0, 0, hz), faces: ['+z'], kind: 'face' },
  ];
  for (const [fx, fy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as const) {
    out.push({
      id: `${fx > 0 ? '+x' : '-x'}${fy > 0 ? '+y' : '-y'}`,
      position: at(fx * hx, fy * hy, hz),
      faces: [fx > 0 ? '+x' : '-x', fy > 0 ? '+y' : '-y'],
      kind: 'corner',
    });
  }
  return out;
}

/** Where a move has got to, e.g. "X 12.4, Y −3.0 m". */
export function moveReadout(to: Vec3, vertical = false): string {
  const n = (v: number) => (Object.is(v, -0) ? 0 : v).toFixed(1);
  return vertical ? `X ${n(to[0])}, Z ${n(to[2])} m` : `X ${n(to[0])}, Y ${n(to[1])} m`;
}

/** What to show while dragging, e.g. "6.0 × 5.0 m" or "x 2.4 m". */
export function dragReadout(box: Box, faces: Face[]): string {
  const name = (a: Axis) => (a === 'x' ? 'Length' : a === 'y' ? 'Width' : 'Height');
  if (faces.length === 1) {
    const a = faceAxis(faces[0]!);
    return `${name(a)} ${box.size[axisIndex(a)].toFixed(1)} m`;
  }
  return `${box.size[0].toFixed(1)} × ${box.size[1].toFixed(1)} m`;
}

/**
 * Which faces the point under the pointer belongs to: the nearest face, plus a second one when the
 * point is near the edge between them, so grabbing an edge or a corner changes two dimensions.
 *
 * `skip` leaves out axes the view cannot judge: looking straight down at a plan, every pointer ray
 * lands on the roof, so the roof must be ignored or it would be the answer every time.
 */
export function facesAtPoint(box: Box, world: Vec3, skip: Axis[] = [], edgeBand = 0.2): Face[] {
  const c = Math.cos(box.yawRad);
  const s = Math.sin(box.yawRad);
  const dx = world[0] - box.center[0];
  const dy = world[1] - box.center[1];
  // into the box's own frame, where each axis can be compared against its own half-size
  const local: Vec3 = [c * dx + s * dy, -s * dx + c * dy, world[2] - box.center[2]];
  const near = (['x', 'y', 'z'] as Axis[])
    .filter((a) => !skip.includes(a))
    .map((a) => {
      const i = axisIndex(a);
      const half = box.size[i] / 2;
      return { face: `${local[i] >= 0 ? '+' : '-'}${a}` as Face, howNear: half > 0 ? Math.abs(local[i]) / half : 0 };
    })
    .sort((p, q) => q.howNear - p.howNear);
  const first = near[0];
  if (!first) return [];
  const second = near[1];
  const edge = second && first.howNear >= 1 - edgeBand && second.howNear >= 1 - edgeBand;
  return edge ? [first.face, second.face] : [first.face];
}

/** Applies a drag measured in world metres to whichever faces are being dragged. */
export function applyResize(box: Box, faces: Face[], worldDelta: Vec3, min = MIN_SIZE_M): Box {
  const alongAxis = (f: Face): number => {
    const d = axisDirection(faceAxis(f), box.yawRad);
    return worldDelta[0] * d[0] + worldDelta[1] * d[1] + worldDelta[2] * d[2];
  };
  const [a, b] = faces;
  if (a && b) return resizeBoxCorner(box, [a, b], alongAxis(a), alongAxis(b), min);
  if (a) return resizeBox(box, a, alongAxis(a), min);
  return box;
}

/** The axes a view cannot judge, because it looks straight along them. */
export function blindAxes(mode: 'persp' | 'plan' | 'elev'): Axis[] {
  if (mode === 'plan') return ['z'];
  if (mode === 'elev') return ['y'];
  return [];
}

/** How far the box reaches along world Y, which depends on its yaw. */
export function halfExtentY(box: Box): number {
  return (Math.abs(Math.sin(box.yawRad)) * box.size[0] + Math.abs(Math.cos(box.yawRad)) * box.size[1]) / 2;
}

/**
 * Where a grab point has to sit to be grabbable. A pointer ray hits the nearest surface, so a grab
 * point left at mid-height is hidden under the roof of its own room in a plan, and inside the near
 * wall in an elevation. Each view brings them out onto the face it is looking at.
 */
export function pickablePosition(box: Box, position: Vec3, mode: 'persp' | 'plan' | 'elev', cameraSide: 1 | -1 = -1, clearance = 0.01): Vec3 {
  if (mode === 'plan') return [position[0], position[1], box.center[2] + box.size[2] / 2 + clearance];
  if (mode === 'elev') return [position[0], box.center[1] + cameraSide * (halfExtentY(box) + clearance), position[2]];
  return position;
}

/** The compass angle of a point about a centre, in radians, measured from +x. */
export const angleAt = (centre: Vec3, world: Vec3): number => Math.atan2(world[1] - centre[1], world[0] - centre[0]);

/** Wraps an angle into (-180°, 180°], so a readout never says 370°. */
export function normaliseYaw(rad: number): number {
  const turn = Math.PI * 2;
  let out = rad % turn;
  if (out > Math.PI) out -= turn;
  if (out <= -Math.PI) out += turn;
  return out;
}

/** Turns the box about its own centre. Snaps to whole degrees, or to `snapDeg` while Shift is held. */
export function rotateBox(box: Box, yawRad: number, snapDeg = 1): Box {
  const step = (snapDeg * Math.PI) / 180;
  return { ...box, yawRad: normaliseYaw(Math.round(yawRad / step) * step) };
}

/** Where the turn knob sits: clear of the box, at roof level so a plan can reach it. */
export function rotateHandleFor(box: Box): Vec3 {
  const pad = Math.max(0.6, Math.min(1.5, Math.min(box.size[0], box.size[1]) * 0.25));
  const out = box.size[1] / 2 + pad;
  return [
    box.center[0] - Math.sin(box.yawRad) * out,
    box.center[1] + Math.cos(box.yawRad) * out,
    box.center[2] + box.size[2] / 2,
  ];
}

export function rotateReadout(box: Box): string {
  const deg = (normaliseYaw(box.yawRad) * 180) / Math.PI;
  return `Turned ${(Math.round(deg * 10) / 10).toFixed(1)}°`;
}
